import { useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, Scale, ShieldCheck, Cookie } from "lucide-react";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { BRAND } from "@/constants/brand";
import { lenisStore } from "@/lib/lenisStore";

const Section = ({ title, children }) => (
  <section className="mt-10">
    <h2 className="font-display text-lg font-bold text-navy sm:text-xl">{title}</h2>
    <div className="mt-3 space-y-3 text-sm leading-relaxed text-slate-600">{children}</div>
  </section>
);

const AVISO = [
  {
    title: "1. Titular del sitio web",
    body: [
      `El presente sitio web es titularidad de ${BRAND.name} (en adelante, «el Titular»), empresa dedicada a la instalación de sistemas de aire acondicionado y climatización, con ámbito de actuación en la provincia de Tarragona y Barcelona.`,
      "NIF: B93960359. Domicilio social: Calle de les Illes Balears 9, 1º A, 43840 Salou, Tarragona (Catalunya, España).",
      `Correo electrónico de contacto: grupofamiliaoliver@gmail.com. Teléfono: ${BRAND.whatsappDisplay}.`,
    ],
  },
  {
    title: "2. Objeto y condiciones de uso",
    body: [
      "El acceso y uso de este sitio web atribuye la condición de usuario e implica la aceptación de las presentes condiciones. El usuario se compromete a hacer un uso lícito, diligente y leal de los contenidos y servicios ofrecidos, absteniéndose de emplearlos para fines contrarios a la ley o a la buena fe.",
      "El titular se reserva el derecho a modificar, suspender o eliminar contenidos y servicios del sitio web sin previo aviso.",
    ],
  },
  {
    title: "3. Propiedad intelectual",
    body: [
      `Todos los contenidos del sitio web (textos, logotipo, imágenes, diseño y código) son titularidad de ${BRAND.name} o se utilizan con la debida autorización, quedando prohibida su reproducción, distribución o transformación sin consentimiento expreso.`,
    ],
  },
  {
    title: "4. Responsabilidad",
    body: [
      "La información publicada sobre servicios, garantías y promociones tiene carácter informativo. Las condiciones definitivas de cada instalación se formalizan siempre mediante presupuesto personalizado y escrito.",
      "El titular no se responsabiliza de los daños derivados de un uso indebido del sitio web ni de los contenidos de sitios de terceros enlazados desde él.",
    ],
  },
  {
    title: "5. Legislación aplicable",
    body: [
      "Las presentes condiciones se rigen por la legislación española. Para cualquier controversia serán competentes los juzgados que correspondan conforme a derecho.",
    ],
  },
];

const PRIVACIDAD = [
  {
    title: "1. Responsable del tratamiento",
    body: [
      `Responsable: ${BRAND.name} · NIF: B93960359. Domicilio social: Calle de les Illes Balears 9, 1º A, 43840 Salou, Tarragona (Catalunya, España). Contacto: grupofamiliaoliver@gmail.com · ${BRAND.whatsappDisplay}. Ámbito de servicio: provincia de Tarragona y Barcelona.`,
    ],
  },
  {
    title: "2. Finalidad del tratamiento",
    body: [
      "Los datos facilitados a través del formulario de presupuesto (nombre, tipo y número de equipos, ciudad, calle, código postal, teléfono y si dispone ya del aparato) se utilizan exclusivamente para gestionar su solicitud de presupuesto, contactar con usted a través de WhatsApp o teléfono y elaborar la propuesta de instalación solicitada.",
    ],
  },
  {
    title: "3. Base jurídica",
    body: [
      "El tratamiento se fundamenta en el consentimiento expreso del interesado (art. 6.1.a del RGPD), otorgado mediante la casilla de aceptación del formulario, y en la aplicación de medidas precontractuales solicitadas por el propio interesado (art. 6.1.b del RGPD).",
    ],
  },
  {
    title: "4. Conservación de los datos",
    body: [
      "Los datos se conservarán mientras sean necesarios para atender la solicitud y, en su caso, durante los plazos legalmente exigibles para el cumplimiento de obligaciones fiscales, contables y de garantía de las instalaciones realizadas.",
    ],
  },
  {
    title: "5. Destinatarios",
    body: [
      "No se cederán datos a terceros, salvo obligación legal. El envío de la solicitud se realiza a través de WhatsApp (Meta Platforms), que actúa como mero canal de comunicación elegido por el propio usuario al pulsar el botón de envío.",
    ],
  },
  {
    title: "6. Derechos del interesado",
    body: [
      "Puede ejercer sus derechos de acceso, rectificación, supresión, oposición, limitación del tratamiento y portabilidad dirigiéndose por escrito a grupofamiliaoliver@gmail.com, adjuntando un documento que acredite su identidad.",
      "Asimismo, tiene derecho a presentar una reclamación ante la Agencia Española de Protección de Datos (www.aepd.es) si considera que el tratamiento no se ajusta a la normativa vigente.",
    ],
  },
  {
    title: "7. Seguridad",
    body: [
      "El responsable aplica medidas técnicas y organizativas apropiadas para garantizar la confidencialidad e integridad de los datos personales tratados a través de este sitio web.",
    ],
  },
];

const COOKIES = [
  {
    title: "1. ¿Qué son las cookies?",
    body: [
      "Las cookies son pequeños archivos que los sitios web guardan en su dispositivo para recordar información sobre su visita y mejorar su experiencia de navegación.",
    ],
  },
  {
    title: "2. Cookies que utiliza este sitio web",
    body: [
      "Cookies técnicas (necesarias): permiten el funcionamiento básico de la web, recuerdan su decisión sobre el aviso de cookies y mantienen la sesión del panel interno de gestión. No requieren consentimiento.",
      "Cookies de análisis: esta web puede utilizar herramientas de medición de uso (PostHog) para conocer de forma agregada cómo se navega por el sitio y poder mejorarlo. Estas cookies solo se emplean si usted acepta el aviso de cookies.",
    ],
  },
  {
    title: "3. Gestión del consentimiento",
    body: [
      "Al entrar por primera vez en la web se muestra un aviso donde puede aceptar o rechazar las cookies no necesarias. Su decisión queda guardada en su navegador. Si desea cambiarla, puede borrar los datos de navegación de este sitio y el aviso volverá a mostrarse.",
    ],
  },
  {
    title: "4. Cómo desactivar las cookies en su navegador",
    body: [
      "Puede permitir, bloquear o eliminar las cookies desde la configuración de su navegador: Chrome (Configuración → Privacidad y seguridad), Firefox (Opciones → Privacidad), Safari (Preferencias → Privacidad) o Edge (Configuración → Cookies). Tenga en cuenta que bloquear las cookies técnicas puede afectar al funcionamiento de la web.",
    ],
  },
  {
    title: "5. Actualizaciones de esta política",
    body: [
      "Esta política puede actualizarse para adaptarse a cambios normativos o técnicos. Le recomendamos revisarla periódicamente. Última actualización: septiembre de 2026.",
    ],
  },
];

export default function Legal({ type }) {
  const META = {
    aviso: { title: "Aviso Legal", eyebrow: "Información legal", Icon: Scale, testid: "aviso-legal-page" },
    privacidad: { title: "Política de Privacidad", eyebrow: "Protección de datos · RGPD", Icon: ShieldCheck, testid: "privacidad-page" },
    cookies: { title: "Política de Cookies", eyebrow: "Uso de cookies", Icon: Cookie, testid: "cookies-page" },
  };
  const meta = META[type] || META.aviso;
  const content = type === "privacidad" ? PRIVACIDAD : type === "cookies" ? COOKIES : AVISO;

  useEffect(() => {
    lenisStore.current?.scrollTo(0, { immediate: true });
    document.title = `${meta.title} | ${BRAND.name}`;
    return () => {
      document.title = `${BRAND.name} | Instalación de Aire Acondicionado con Garantía Certificada`;
    };
  }, [meta.title]);

  return (
    <div data-testid={meta.testid}>
      <Navbar />
      <main className="bg-ocean-50/50 pb-20 pt-32 lg:pt-40">
        <div className="mx-auto max-w-3xl px-4 sm:px-6 lg:px-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
          >
            <Link
              to="/"
              data-testid="legal-back-link"
              className="inline-flex items-center gap-2 text-sm font-medium text-slate-500 transition-colors hover:text-ocean-600"
            >
              <ArrowLeft className="h-4 w-4" />
              Volver a la página principal
            </Link>

            <div className="mt-8 rounded-3xl border border-slate-200 bg-white p-6 shadow-xl shadow-navy/5 sm:p-10">
              <span className="inline-flex items-center gap-2 rounded-full border border-ocean-600/20 bg-ocean-50 px-4 py-1.5 text-xs font-semibold uppercase tracking-[0.18em] text-ocean-700">
                <meta.Icon className="h-3.5 w-3.5" />
                {meta.eyebrow}
              </span>
              <h1 className="mt-5 font-display text-2xl font-bold tracking-tight text-navy sm:text-3xl lg:text-4xl text-balance">
                {meta.title}
              </h1>
              <p className="mt-3 text-sm text-slate-500">
                Última actualización: septiembre de 2026 · {BRAND.name}
              </p>

              {content.map((s) => (
                <Section key={s.title} title={s.title}>
                  {s.body.map((p, i) => (
                    <p key={i}>{p}</p>
                  ))}
                </Section>
              ))}
            </div>
          </motion.div>
        </div>
      </main>
      <Footer />
    </div>
  );
}
